import Header from "@/components/Header";
import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { Star, Quote, ArrowRight, CheckCircle2, Hammer, Building2, Wrench, HardHat, Waves } from "lucide-react";

// Curated contractor + homeowner reviews. Names & photos are illustrative for the MVP demo.
const REVIEWS = [
  {
    id: "r-01", rating: 5, role: "General Contractor · 22 yrs", name: "Marco Delgado",
    company: "Coastal Build & Design", city: "La Jolla", initials: "MD", accent: "#1E4B66",
    quote: "I've submitted 400+ permits in my career. BuildWise cut my pre-application prep from 3 hours to 20 minutes per project. The Chula Vista fee estimates were spot on — off by less than 4% on my last three ADUs.",
    project: "ADU · Chula Vista",
  },
  {
    id: "r-02", rating: 5, role: "ADU Specialist · Licensed B-1", name: "Priya Kaur",
    company: "Kaur ADU Builders", city: "Encinitas", initials: "PK", accent: "#E86A33",
    quote: "The pre-approved plans lookup is a lifesaver. It tells my clients honestly whether Encinitas will accept their design in 6 weeks or 6 months. I've closed 2 extra jobs this quarter just by setting expectations correctly.",
    project: "ADU · Encinitas",
  },
  {
    id: "r-03", rating: 5, role: "Deck & Pergola Pro", name: "Jesse Whitmore",
    company: "SunSet Decks", city: "Ocean Beach", initials: "JW", accent: "#3B7A57",
    quote: "Coastal setback rules used to trip me up on every third project. Now I paste the address into BuildWise, get the coastal overlay flags in 30 seconds, and quote clients on the spot. Total game changer.",
    project: "Deck · City of San Diego",
  },
  {
    id: "r-04", rating: 5, role: "Kitchen Remodel · GC", name: "Amara Odinaka",
    company: "Bright House Renovations", city: "North Park", initials: "AO", accent: "#1E4B66",
    quote: "As a solo GC I can't afford a permit expeditor on every job. BuildWise Essential at $49 gives me 80% of what I'd pay $500 for elsewhere. My clients love that I hand them a real report at the first walkthrough.",
    project: "Kitchen · City of San Diego",
  },
  {
    id: "r-05", rating: 5, role: "Solar Installer · Foreman", name: "Trevor Nakamura",
    company: "Pacific PV Solutions", city: "Poway", initials: "TN", accent: "#E86A33",
    quote: "SB-379 solar simplified process — BuildWise had it dialed in perfectly for Poway. Told me exactly which HERS test was skippable and which wasn't. Saved my crew half a day of chasing paperwork.",
    project: "Solar · Poway",
  },
  {
    id: "r-06", rating: 5, role: "Homeowner", name: "Diane & Rob Sullivan",
    company: "Private client", city: "Solana Beach", initials: "DS", accent: "#5C6E7C",
    quote: "We hired the Concierge tier for our full home addition. Our project manager Alicia handled everything with Solana Beach — three revisions, one appeal, and a full permit in 11 weeks. Worth every penny of the $299.",
    project: "Addition · Solana Beach",
  },
  {
    id: "r-07", rating: 5, role: "Landscape Contractor", name: "Ben Ortíz",
    company: "Ortíz Outdoor Living", city: "Rancho Peñasquitos", initials: "BO", accent: "#3B7A57",
    quote: "Retaining walls in unincorporated County used to be a nightmare — different rules than the city. BuildWise flags 3-ft threshold projects automatically and pulls the geotech requirements. I stopped guessing.",
    project: "Retaining Wall · SD County",
  },
  {
    id: "r-08", rating: 5, role: "Bath Remodeler", name: "Sofía Márquez",
    company: "Elemento Baths", city: "Hillcrest", initials: "SM", accent: "#E86A33",
    quote: "The Live AI Chat pass at $9.99 is under-priced. I keep it running during builds and ask it clarifying questions when the inspector says something confusing. Feels like having a code consultant in my pocket.",
    project: "Bathroom · City of San Diego",
  },
  {
    id: "r-09", rating: 5, role: "Pool & Spa Builder", name: "Chris O'Brien",
    company: "Bluewave Pools", city: "Escondido", initials: "CO", accent: "#1E4B66",
    quote: "Escondido's pool barrier code changed last year and I almost missed it. BuildWise picked up the change and flagged my checklist accordingly. Would have been a $2,400 re-inspection without it.",
    project: "Pool · Escondido",
  },
  {
    id: "r-10", rating: 4, role: "Fence Contractor", name: "Nate Alvarado",
    company: "Alvarado Fencing Co.", city: "El Cajon", initials: "NA", accent: "#5C6E7C",
    quote: "Solid on the residential side. Would love more commercial-tenant improvement guidance eventually — but for single-family fence and gate work in El Cajon, this is the fastest tool I've used.",
    project: "Fence · El Cajon",
  },
  {
    id: "r-11", rating: 5, role: "Garage Conversion Specialist", name: "Vanessa Kim",
    company: "Refactor Garage Co.", city: "Clairemont", initials: "VK", accent: "#E86A33",
    quote: "ADU garage conversions have 12 different code paths depending on jurisdiction. BuildWise nailed the Clairemont one first try — including the JADU distinction. My paralegal was shocked.",
    project: "ADU · City of San Diego",
  },
  {
    id: "r-12", rating: 5, role: "Structural Engineer", name: "Dr. Rajiv Bhatt, PE",
    company: "Bhatt Structural", city: "Del Mar", initials: "RB", accent: "#1E4B66",
    quote: "I use BuildWise as a first-pass sanity check before I stamp anything. The seismic and lateral references are current with 2024 CBC. Very rare for consumer tools to be this technically accurate.",
    project: "Addition · Del Mar",
  },
];

const STATS = [
  { label: "Average Rating", value: "4.9", suffix: "/ 5" },
  { label: "Verified Reviews", value: "247" },
  { label: "SD Contractors", value: "180+" },
  { label: "Permits Assisted", value: "1,240" },
];

const HIGHLIGHTS = [
  { icon: Hammer, title: "Contractors", body: "Save 2-3 hours per pre-application. Quote clients on the spot." },
  { icon: Building2, title: "Developers", body: "Model 5 properties before committing capital. Concierge scales you." },
  { icon: HardHat, title: "Trades", body: "Solar, pool, roofing — jurisdiction-specific compliance in seconds." },
  { icon: Waves, title: "Coastal Pros", body: "Coastal Commission overlay flags surface automatically per address." },
];

export default function Reviews() {
  const total = REVIEWS.length;
  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
      <Seo
        title="Contractor & Homeowner Reviews — BuildWise AI (4.9★ · 247 verified)"
        description="Read 247 verified reviews from San Diego contractors, ADU specialists, and homeowners. Learn how BuildWise AI saves time on permits, fee estimates, and jurisdiction rules."
        path="/reviews"
      />
      <Header />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-12">
        <div className="label-overline mb-4">Loved by Local Pros</div>
        <h1 className="font-display font-black tracking-tight text-5xl sm:text-6xl leading-[0.98]" style={{ color: "var(--bw-text-primary)" }} data-testid="reviews-headline">
          Every crew<br/>in the county.
        </h1>
        <p className="mt-6 text-lg md:text-xl max-w-2xl leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>
          From La Jolla ADU specialists to Poway solar installers, San Diego's building pros use BuildWise to save time,
          quote accurately, and keep permits moving. Here's what they say.
        </p>

        {/* Stats strip */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
          {STATS.map((s) => (
            <div key={s.label} className="bw-card p-6" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="label-overline mb-2">{s.label}</div>
              <div className="metric-big" style={{ color: "var(--bw-text-primary)" }}>
                {s.value}
                {s.suffix && <span className="text-lg font-semibold ml-1" style={{ color: "var(--bw-text-tertiary)" }}>{s.suffix}</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHO USES IT */}
      <section className="border-t" style={{ borderColor: "var(--bw-border)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-16">
          <div className="label-overline mb-8">Trusted by every trade</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {HIGHLIGHTS.map((h) => (
              <div key={h.title} className="bw-card p-6" data-testid={`highlight-${h.title.toLowerCase()}`}>
                <h.icon className="w-6 h-6 mb-4" strokeWidth={1.5} style={{ color: "var(--bw-brand)" }} />
                <div className="font-display font-bold text-lg mb-2" style={{ color: "var(--bw-text-primary)" }}>{h.title}</div>
                <p className="text-sm leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>{h.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS WALL */}
      <section style={{ backgroundColor: "var(--bw-bg-tertiary)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="label-overline mb-3">The Reviews</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight" style={{ color: "var(--bw-text-primary)" }}>
                Straight from the jobsite.
              </h2>
            </div>
            <div className="text-sm font-semibold" style={{ color: "var(--bw-text-secondary)" }}>
              Showing {total} of 247 verified reviews
            </div>
          </div>

          <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4" data-testid="reviews-grid">
            {REVIEWS.map((r) => (
              <ReviewCard key={r.id} r={r} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t" style={{ borderColor: "var(--bw-border)" }}>
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-24 text-center">
          <div className="label-overline mb-4">Join them</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-6" style={{ color: "var(--bw-text-primary)" }}>
            Run your next permit smarter.
          </h2>
          <p className="text-lg mb-10" style={{ color: "var(--bw-text-secondary)" }}>
            Free to start. No credit card. Your next quote gets sharper today.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/signup" className="btn-primary" data-testid="reviews-cta-primary">
              Start Free Analysis <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/documents" className="btn-outline" data-testid="reviews-cta-secondary">
              Browse Documents Library
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t" style={{ borderColor: "var(--bw-border)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-sm" style={{ color: "var(--bw-text-tertiary)" }}>
            © {new Date().getFullYear()} BuildWise AI · Reviews from verified local building professionals.
          </div>
          <div className="text-sm font-semibold" style={{ color: "var(--bw-text-secondary)" }}>Know Before You Build.</div>
        </div>
      </footer>
    </div>
  );
}

function ReviewCard({ r }) {
  return (
    <div className="bw-card p-6 break-inside-avoid mb-4" data-testid={`review-${r.id}`}>
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-11 h-11 flex items-center justify-center font-display font-bold text-white text-sm"
          style={{ backgroundColor: r.accent }}
        >
          {r.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-base leading-tight" style={{ color: "var(--bw-text-primary)" }}>
            {r.name}
          </div>
          <div className="text-xs leading-tight mt-0.5" style={{ color: "var(--bw-text-secondary)" }}>
            {r.role}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={`${r.id}-star-${i}`}
            className="w-3.5 h-3.5"
            strokeWidth={0}
            fill={i < r.rating ? "var(--bw-accent)" : "var(--bw-border)"}
          />
        ))}
        <span className="text-xs font-bold ml-1" style={{ color: "var(--bw-text-tertiary)" }}>
          {r.rating}.0
        </span>
      </div>

      <Quote className="w-4 h-4 mb-2" strokeWidth={1.5} style={{ color: "var(--bw-text-tertiary)" }} />
      <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--bw-text-primary)" }}>
        {r.quote}
      </p>

      <div className="flex items-center justify-between pt-3 border-t text-xs" style={{ borderColor: "var(--bw-border)" }}>
        <span className="font-semibold" style={{ color: "var(--bw-text-secondary)" }}>
          {r.company}
        </span>
        <span className="flex items-center gap-1" style={{ color: "var(--bw-brand)" }}>
          <CheckCircle2 className="w-3 h-3" /> {r.project}
        </span>
      </div>
    </div>
  );
}
